import { useEffect, useState } from 'react';
import {
  categoryType,
  RouteState,
  useCreateAiWorkersMutation,
  useUpdateAiWorkersMutation,
} from '@circuitry-ai/doc-data';
import { checkType, CtyButton, CtyShowToast, CopyCodeField, optionalCodePayload } from '@circuitry-ai/doc-ui';
import { Input, Textarea } from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { MapperItem } from '@circuitry-ai/doc-data';

interface SchemaMapperConfigurationProps {
  readonly mode?: string;
  readonly mapper: MapperItem | null;
}

export default function SchemaMapperConfiguration({ mode, mapper }: Readonly<SchemaMapperConfigurationProps>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isCreateMode = mode === RouteState.CREATE;
  const [mapperCode, setMapperCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const [createAiWorkers, { isLoading: isCreating }] = useCreateAiWorkersMutation();
  const [updateAiWorkers, { isLoading: isUpdating }] = useUpdateAiWorkersMutation();

  const navigateToMapperTab = (mapperId: string) => {
    navigate(`/settings/schema-mapper/${RouteState.UPDATE}/${mapperId}?tab=mapper`);
  };

  useEffect(() => {
    if (!mapper) return;
    setMapperCode(mapper.code ?? '');
    setName(mapper.name ?? '');
    setDescription(mapper.description ?? '');
  }, [mapper]);

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      CtyShowToast(checkType.warning, t('Please enter mapper name'));
      return;
    }

    if (!description.trim()) {
      CtyShowToast(checkType.warning, t('Please enter mapper description'));
      return;
    }

    try {
      if (isCreateMode) {
        const response = await createAiWorkers({
          category: categoryType.MAPPER,
          type: 'product',
          sub_type: 'mapper',
          name: trimmedName,
          description: description.trim(),
          aiworkers_attrs: [],
          ...optionalCodePayload(mapperCode),
        }).unwrap();

        const createdMapperId = String(
          (response as { data?: { aiworker_id?: string }; aiworker_id?: string })?.data
            ?.aiworker_id ??
            (response as { data?: { aiworker_id?: string }; aiworker_id?: string })?.aiworker_id ??
            ''
        );

        if (!createdMapperId) {
          CtyShowToast(checkType.error, t('Mapper ID not found'));
          return;
        }

        CtyShowToast(checkType.success, t('Mapper created successfully'));
        navigateToMapperTab(createdMapperId);
      } else {
        const mapperId = mapper?.aiworker_id;
        if (!mapperId) {
          CtyShowToast(checkType.error, t('Mapper ID not found'));
          return;
        }
        await updateAiWorkers({
          aiworker_id: mapperId,
          payLoad: {
            name: trimmedName,
            description: description.trim(),
          },
        }).unwrap();
        CtyShowToast(checkType.success, t('Mapper updated successfully'));
        navigateToMapperTab(mapperId);
      }
    } catch (error) {
      CtyShowToast(checkType.error, (error as Error)?.message ?? t('Save operation failed'));
    }
  };

  return (
    <div className="flex flex-col h-full gap-3 pt-2 pb-3">
      <div className="flex-1 overflow-y-auto w-full px-5">
        <div className="md:w-[70%] flex flex-col gap-3">
          <CopyCodeField
            label="Code"
            value={mapperCode}
            layout="horizontal"
            isReadOnly={!isCreateMode}
            isRequired={false}
            onValueChange={isCreateMode ? setMapperCode : undefined}
          />
          <div className="flex flex-row gap-3">
            <label htmlFor="mapper_name" className="flex flex-col w-[30%]">
              <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                {t('Name')}
                <span className="text-danger">*</span>
              </span>
            </label>
            <div className="flex-1">
              <Input
                id="mapper_name"
                type="text"
                size="sm"
                aria-label={t('Mapper name')}
                value={name}
                isRequired
                onValueChange={setName}
              />
            </div>
          </div>

          <div className="flex flex-row gap-3">
            <label htmlFor="mapper_description" className="flex flex-col w-[30%]">
              <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                {t('Description')}
                <span className="text-danger">*</span>
              </span>
            </label>
            <div className="flex-1">
              <Textarea
                id="mapper_description"
                minRows={5}
                maxRows={7}
                aria-label={t('Mapper description')}
                value={description}
                onValueChange={setDescription}
                isRequired
              />
            </div>
          </div>

        </div>

        <div className="flex justify-end md:w-[70%] mt-3">
          <CtyButton
            variant="solid"
            color="primary"
            size="md"
            radius="sm"
            onPress={handleSave}
            isLoading={isCreating || isUpdating}
          >
            {isCreateMode ? t('Save') : t('Update')}
          </CtyButton>
        </div>
      </div>
    </div>
  );
}
